
import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import {Mail, Phone, MapPin, Instagram, Facebook, Twitter, MessageSquare} from 'lucide-react'

const Footer: React.FC = () => {
  const location = useLocation()
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'zh'

  const content = {
    zh: {
      company: {
        name: 'ONGU STUDIO 醞玖製研',
        tagline: 'Origin. Natural. Grow. Unique. This is ONGU. Within a bean, a soul unearthed.',
        description: '專業咖啡烘焙，提供B2C線上購物、多分店門市、B2B批發服務與完整帳務管理系統。'
      },
      links: {
        shop: '線上購物',
        store: '實體門市',
        lab: '醞玖製研中心',
        wholesale: 'B2B批發',
        faq: '常見問題',
        about: '關於我們',
        contact: '聯絡我們',
        privacy: '隱私政策',
        terms: '服務條款',
        sitemap: '網站地圖'
      },
      contact: {
        title: '聯絡資訊',
        address: '110臺北市信義區信義路四段389號1樓',
        phone: '0938 510 307',
        email: 'service@ongucoffee.com'
      },
      hours: {
        title: '營業時間',
        weekday: '週一至五：09:00–17:00',
        saturday: '週六：10:00–17:00',
        sunday: '週日公休'
      },
      copyright: '版權所有',
      allRights: '保留所有權利',
      designBy: 'Design By'
    },
    en: {
      company: {
        name: 'ONGU STUDIO',
        tagline: 'Origin. Natural. Grow. Unique. This is ONGU. Within a bean, a soul unearthed.',
        description: 'Professional coffee roasting with B2C online shopping, multi-store operations, B2B wholesale services and integrated financial management.'
      },
      links: {
        shop: 'Shop',
        store: 'Stores',
        lab: 'ONGU Lab',
        wholesale: 'Wholesale',
        faq: 'FAQ',
        about: 'About Us',
        contact: 'Contact',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
        sitemap: 'Sitemap'
      },
      contact: {
        title: 'Contact Info',
        address: '1F, No.389, Sec.4, Xinyi Rd., Xinyi Dist., Taipei City 110',
        phone: '0938 510 307',
        email: 'service@ongucoffee.com'
      },
      hours: {
        title: 'Business Hours',
        weekday: 'Mon-Fri: 09:00–17:00',
        saturday: 'Sat: 10:00–17:00',
        sunday: 'Sun: Closed'
      },
      copyright: 'Copyright',
      allRights: 'All rights reserved',
      designBy: 'Design By'
    }
  }

  const t = content[currentLang]

  return (
    <footer className="footer-industrial border-t border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* Main Footer Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px] py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src="https://static.lumi.new/f4/f41cec5bb6b72110e177830b0f994a38.png"
                alt="ONGU STUDIO Logo"
                className="h-8 w-auto object-contain"
              />
              <div>
                <div className="text-lg font-light tracking-wider text-gray-900 dark:text-gray-100">
                  {t.company.name}
                </div>
              </div>
            </div>
            
            <p className="text-gray-700 dark:text-gray-200 mb-4 font-light italic">
              {t.company.tagline}
            </p>
            
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-6 max-w-md">
              {t.company.description}
            </p>

            {/* Social Links */}
            <div className="flex space-x-4">
              <a
                href="https://www.instagram.com/onguroasters/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors duration-200"
              >
                <Instagram size={14} className="text-gray-700 dark:text-gray-200" />
              </a>
              <a
                href="https://www.facebook.com/ongustudio/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors duration-200"
              >
                <Facebook size={14} className="text-gray-700 dark:text-gray-200" />
              </a>
              <a
                href="https://line.me/R/ti/p/@993lrijj"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gray-200 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors duration-200"
              >
                <MessageSquare size={14} className="text-gray-700 dark:text-gray-200" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-light tracking-wider text-gray-900 dark:text-gray-100 uppercase mb-6">
              {currentLang === 'zh' ? '快速連結' : 'Quick Links'}
            </h3>
            <nav className="space-y-3">
              <Link
                to={`/${currentLang}/shop`}
                onClick={() => window.scrollTo(0, 0)}
                className="block text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
              >
                {t.links.shop}
              </Link>
              <Link
                to={`/${currentLang}/stores`}
                onClick={() => window.scrollTo(0, 0)}
                className="block text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
              >
                {t.links.store}
              </Link>
              <Link
                to={`/${currentLang}/lab`}
                onClick={() => window.scrollTo(0, 0)}
                className="block text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
              >
                {t.links.lab}
              </Link>
              <Link
                to={`/${currentLang}/wholesale`}
                onClick={() => window.scrollTo(0, 0)}
                className="block text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
              >
                {t.links.wholesale}
              </Link>
              <Link
                to={`/${currentLang}/faq`}
                onClick={() => window.scrollTo(0, 0)}
                className="block text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
              >
                {t.links.faq}
              </Link>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-light tracking-wider text-gray-900 dark:text-gray-100 uppercase mb-6">
              {t.contact.title}
            </h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin size={14} className="text-gray-500 dark:text-gray-400 mt-1 flex-shrink-0" />
                <span className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {t.contact.address}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone size={14} className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <a
                  href={`tel:${t.contact.phone}`}
                  className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
                >
                  {t.contact.phone}
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail size={14} className="text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <a
                  href={`mailto:${t.contact.email}`}
                  className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
                >
                  {t.contact.email}
                </a>
              </div>
            </div>

            {/* Business Hours */}
            <div className="mt-6">
              <h4 className="text-sm font-light tracking-wider text-gray-900 dark:text-gray-100 uppercase mb-3">
                {t.hours.title}
              </h4>
              <div className="space-y-1 text-sm text-gray-900 dark:text-gray-300">
                <div>{t.hours.weekday}</div>
                <div>{t.hours.saturday}</div>
                <div>{t.hours.sunday}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-300 dark:border-gray-700">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px] py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              © 2025 ONGU STUDIO. {t.copyright} © {t.allRights}. {t.designBy}{' '}
              <a 
                href="https://www.foonplay.io" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-gray-900 dark:hover:text-gray-200 transition-colors duration-200"
              >
                Foonplay, 網頁設計天辰寰球
              </a>.
            </div>
            <div className="flex space-x-6">
              <Link
                to={`/${currentLang}/privacy`}
                onClick={() => window.scrollTo(0, 0)}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors duration-200"
              >
                {t.links.privacy}
              </Link>
              <Link
                to={`/${currentLang}/terms`}
                onClick={() => window.scrollTo(0, 0)}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors duration-200"
              >
                {t.links.terms}
              </Link>
              <Link
                to={`/${currentLang}/sitemap`}
                onClick={() => window.scrollTo(0, 0)}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors duration-200"
              >
                {t.links.sitemap}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Structured Data for LocalBusiness */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "ONGU STUDIO",
            "description": "專業咖啡烘焙，提供B2C線上購物、多分店門市、B2B批發服務",
            "url": "https://ongu-studio.com",
            "telephone": "0938 510 307",
            "email": "ongu@foonplay.io",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "信義路四段389號1樓",
              "addressLocality": "台北市",
              "addressRegion": "信義區",
              "postalCode": "110",
              "addressCountry": "TW"
            },
            "openingHours": [
              "Mo-Fr 09:00-17:00",
              "Sa 10:00-17:00"
            ],
            "priceRange": "$$",
            "servesCuisine": "Coffee"
          })
        }}
      />
    </footer>
  )
}

export default Footer
